import { Injectable, inject } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { Chart, ChartConfiguration } from 'chart.js';
import { GlobalsStore } from '../../stores/globals.state';
import { DataEntry } from '../../models/indicator-data';
import {
  Indicator,
  getUnit,
  isWegweiserIndicator,
} from '../../models/indicator';
import { ExtendedChartData } from '../../models/chartjs-extensions';
import { WidgetConfig } from '../grid-widget-config.service';
import { TooltipHelperService } from '../widget/tooltip-helper.service';
import { generateHarmoniousColors } from '../../utility/color.utility';
import { fallbackColors } from '../../models/colorset';

@Injectable({
  providedIn: 'root',
})
export class MapTooltipService {
  private readonly globalsStore = inject(GlobalsStore);
  private readonly tooltipHelperService = inject(TooltipHelperService);

  /**
   * Generates a tooltip for a Leaflet map feature based on the provided feature, value, captions, and additional parameters.
   *
   * @param {any} feature The map feature object that contains properties used in the tooltip content.
   * @param {number | null} value The numeric value to be displayed in the tooltip. Can be null if no value is available.
   * @param {any} captions An object specifying which parts (key, name, value) should be included in the tooltip content.
   * @param {string} indicatorId The identifier of the indicator used for formatting the value.
   * @param {string} unit The unit of measurement for the value displayed in the tooltip.
   * @param {boolean} [isWegweiserIndicator=false] Indicates whether the value is associated with a "Wegweiser" indicator, altering its formatting.
   * @return {HTMLElement | null} A DOM element containing the tooltip content, or null if the captions are not provided or if the resulting tooltip content is empty.
   */
  generateLeafletTooltip(
    feature: any,
    value: number | null,
    captions: any,
    indicatorId: string,
    unit: string,
    isWegweiserIndicator = false
  ): HTMLElement | null {
    if (!captions) return null;

    const germanNumber = this.globalsStore.getFormattedValue(
      value,
      indicatorId,
      false,
      unit,
      'short',
      isWegweiserIndicator
    );

    const keyHTML = captions.key
      ? '<div><strong>' +
        (feature.properties.key || feature.properties.gkz) +
        '</strong></div>'
      : '';
    const nameHTML = captions.name
      ? '<div>' + feature.properties.name + '</div>'
      : '';
    const valueHTML =
      value && captions.value ? '<div>' + germanNumber + '</div>' : '';

    const html = keyHTML + nameHTML + valueHTML;
    const content = document.createElement('div');
    content.classList.add('tooltip-content');
    content.innerHTML = html;

    return html.trim() ? content : null;
  }

  /**
   * Builds an HTML string representing a popup for a point of interest (POI).
   *
   * @param {string} typeName - The name representing the type or category of the POI.
   * @param {any} poi - The POI object containing relevant properties such as name, description, and others.
   * @param {SafeHtml} [icon] - An optional parameter for providing a SafeHtml icon to display in the popup.
   * @param {DataEntry[]} [data] - Optional data entries used to display indicator-related values within the popup.
   * @param {Indicator[]} [indicators] - Optional array of indicators used to interpret and display corresponding data for the POI.
   * @return {string} Returns an HTML string that represents the fully constructed POI popup.
   */
  buildPoiPopup(
    typeName: string,
    poi: any,
    icon?: SafeHtml,
    data?: DataEntry[],
    indicators?: Indicator[]
  ): string {
    const generateHeader = (): string =>
      `<h3 class="flex gap-3 align-items-center flex-wrap-reverse">
        ${poi.properties.name || ''}
        <span class="m-1 absolute top-0 left-0 text-xs p-chip py-1">${typeName}</span>
      </h3>`;

    const generatePropertyEntry = (label: string, value?: string): string =>
      value ? `<h4 class="mt-3 mb-1">${label}:</h4><div>${value}</div>` : '';

    const generateIndicatorData = (): string => {
      if (
        !data ||
        !indicators ||
        data.length === 0 ||
        indicators.length === 0
      ) {
        return '';
      }

      const key = poi.properties?.key;
      let indicatorHTML =
        '<div class="mt-3"><h4 class="mb-2">Indikatoren:</h4>';

      indicators.forEach((indicator, index) => {
        if (data[index] && key) {
          const value = data[index][key];
          if (value !== null && value !== undefined) {
            const unit = getUnit(indicator);
            indicatorHTML += `
              <div class="flex justify-content-between mb-1">
                <span>${indicator.name}:</span>
                <strong>${value}${unit ? ' ' + unit : ''}</strong>
              </div>
            `;
          }
        }
      });

      indicatorHTML += '</div>';
      return indicatorHTML;
    };

    const bodyContent = `
      ${poi.properties.description ? `<i>${poi.properties.description}</i>` : ''}
      ${generateIndicatorData()}
      ${generatePropertyEntry('Adresse', poi.properties.address)}
      ${generatePropertyEntry('Kontakt', poi.properties.contact)}
      ${generatePropertyEntry('Altersklasse', poi.properties.ageClass)}
      ${generatePropertyEntry('Plätze', poi.properties.places)}
      ${generatePropertyEntry('Öffnungszeiten', poi.properties.openingTimes)}
      ${generatePropertyEntry('Besonderheit', poi.properties.specials)}
      ${
        poi.properties.information
          ? generatePropertyEntry(
              'Informationen',
              poi.properties.information.replace(/href/g, 'target="blank" href')
            )
          : ''
      }
    `;

    return `<div class="poi-popup">
        ${generateHeader()}
        ${bodyContent}
      </div>`;
  }

  /**
   * Generates a bar chart on a map canvas for the specified feature, based on the provided chart data and configuration.
   *
   * @param {string} id - A unique identifier for the chart, used to generate the canvas element's ID.
   * @param {any} feature - The geographical feature for which the chart is being generated, containing properties such as `name` and `key`.
   * @param {WidgetConfig} config - The configuration object for the widget, containing specific settings for the chart's behavior and appearance.
   * @param {ExtendedChartData} [chartData] - The data to be displayed on the chart, including labels and datasets. This parameter is optional.
   * @param {string[]} [colors] - An optional array of colors used to customize the chart's appearance.
   * @return {HTMLCanvasElement | null} The generated canvas element containing the chart, or `null` if the input data is insufficient or invalid.
   */
  generateOnMapChart(
    id: string,
    feature: any,
    config: WidgetConfig,
    chartData?: ExtendedChartData,
    colors?: string[]
  ): HTMLCanvasElement | null {
    const canvas = document.createElement('canvas');
    canvas.style.height = '80px';
    canvas.style.width = '100px';
    canvas.id = `chart-${id}--${feature.properties.key}`;

    let dataIdx = 0;
    if (!chartData || !chartData.labels || !chartData.datasets) return null;

    const labels = chartData.labels.filter((label: any, idx) => {
      if (label === feature.properties.name) {
        dataIdx = idx;
        return true;
      }
      return false;
    });

    const datasets = chartData.datasets.map((dataset: any) => ({
      ...dataset,
      data: [...dataset.data],
    }));

    datasets.shift();
    const result: any = datasets.map((data: any, idx) => {
      const values: any[] = data.data;
      const newValues: any[] = [values.at(dataIdx)];
      const harmonicColorset = generateHarmoniousColors(
        colors || fallbackColors
      );
      const color = harmonicColorset?.at(idx);
      return {
        data: newValues,
        label: data.label,
        backgroundColor: color,
        borderColor: color,
      };
    });

    const chart: ChartConfiguration = {
      type: 'bar',
      data: {
        labels,
        datasets: result,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            display: false,
          },
          datalabels: { display: false },
          tooltip: {
            enabled: false,
            external: (context: any) => {
              this.tooltipHelperService.createOrUpdateTooltip(
                context.tooltip,
                'chart-js--tooltip-container',
                'bar',
                config,
                true
              );
            },
          },
        },
        scales: {
          x: { display: false },
          y: { display: true },
        },
      },
    };

    setTimeout(() => {
      new Chart(canvas, chart);
    }, 250);

    return canvas;
  }
}
